import { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "react-query";
import type { ColumnDef, ColumnFiltersState, PaginationState, SortingState } from "@tanstack/react-table";
import { api } from "@/lib/api";
import { useAuth } from "@/context/auth.context";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import DataTable from "@/components/datatable/Datatable";
import DataTableColumnHeader from "@/components/datatable/DataTableColumnHeader";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { format, endOfMonth, differenceInDays } from "date-fns";
import {
  HandCoins,
  PiggyBank,
  Calendar,
  Clock,
  X,
  AlertTriangle,
  ArrowRight,
  Loader2,
  DollarSign,
  TrendingUp,
  Info,
} from "lucide-react";

const DEFAULT_SHARE_PRICE = 10000;
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const NORMALIZED_APPROVED = ["Verified", "Completed", "Deducted from Salary", "Submitted to HR", "Confirmed by Accountant", "Approved by President", "Verified by Secretary", "Approved"];
const NORMALIZED_CANCELLED = ["Cancelled", "Rejected"];

function normalizeStatus(s: string): "pending" | "approved" | "cancelled" {
  const st = String(s || "").trim();
  if (NORMALIZED_APPROVED.includes(st)) return "approved";
  if (NORMALIZED_CANCELLED.includes(st)) return "cancelled";
  return "pending";
}

function safeNumber(v: any): number {
  if (v == null) return 0;
  if (typeof v === "number" && !Number.isNaN(v)) return v;
  const n = Number(v);
  return Number.isNaN(n) ? 0 : n;
}

function formatShareCount(value: number): string {
  if (!Number.isFinite(value)) return "0";
  const rounded = Math.round(value * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function getLastApprovalInfo(c: any): { approvedBy: string; approvalDate: string } {
  const fields = [
    { by: c.completedBy, at: c.completedAt },
    { by: c.deductedBy, at: c.deductedAt },
    { by: c.submittedToHRBy, at: c.submittedToHRAt },
    { by: c.accountantConfirmedBy, at: c.accountantConfirmedAt },
    { by: c.presidentApprovedBy, at: c.presidentApprovedAt },
    { by: c.secretaryVerifiedBy, at: c.secretaryVerifiedAt },
    { by: c.verifiedBy, at: c.verifiedAt },
  ];
  let lastBy = "";
  let lastAt = "";
  for (const f of fields) {
    if (f.by) { lastBy = f.by; lastAt = f.at || ""; }
  }
  return { approvedBy: lastBy, approvalDate: lastAt };
}

export default function MyContributions() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: settingsData } = useQuery(
    ["my-contributions-settings"],
    async () => {
      const { data } = await api.get("/settings");
      return data.data || data;
    },
    { staleTime: 300000 }
  );

  const now = new Date();
  const group = (user as any)?.group || {};
  const savingsSettings = settingsData?.savings || {};
  const sharePrice = safeNumber(savingsSettings.shareValue) || safeNumber(group.pricePerShare) || DEFAULT_SHARE_PRICE;
  const requiredShares = safeNumber(savingsSettings.minimumSharesRequired) || safeNumber(savingsSettings.minSharesRequired) || safeNumber(group.minShares) || 1;
  const monthlyCommitment = safeNumber(savingsSettings.minMonthlyContribution) || (sharePrice * requiredShares) || safeNumber(group.solidarityAmount) || DEFAULT_SHARE_PRICE;

  const getContributionShares = useCallback((contribution: any) => {
    const amount = safeNumber(contribution?.amount);
    if (sharePrice > 0 && amount > 0) return amount / sharePrice;
    return safeNumber(contribution?.shareCount);
  }, [sharePrice]);

  const [historySearch, setHistorySearch] = useState("");
  const [historyColumnFilters, setHistoryColumnFilters] = useState<ColumnFiltersState>([]);
  const [historySorting, setHistorySorting] = useState<SortingState>([
    { id: "createdAt", desc: true },
  ]);
  const [historyPagination, setHistoryPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const [formMonth, setFormMonth] = useState<string>(String(now.getMonth()));
  const [formRequestDate, setFormRequestDate] = useState<string>(format(now, "yyyy-MM-dd"));
  const [formDeductionDate, setFormDeductionDate] = useState<string>(format(endOfMonth(now), "yyyy-MM-dd"));
  const [formAmount, setFormAmount] = useState<string>("");
  const [isCustomAmount, setIsCustomAmount] = useState(false);

  const formAmountNum = useMemo(() => {
    const n = Number(formAmount);
    return Number.isNaN(n) ? 0 : n;
  }, [formAmount]);

  const formShares = useMemo(() => {
    if (sharePrice <= 0 || formAmountNum <= 0) return 0;
    return Math.floor(formAmountNum / sharePrice);
  }, [formAmountNum, sharePrice]);

  const resetForm = useCallback(() => {
    setFormMonth(String(now.getMonth()));
    setFormRequestDate(format(now, "yyyy-MM-dd"));
    setFormDeductionDate(format(endOfMonth(now), "yyyy-MM-dd"));
    setFormAmount("");
    setIsCustomAmount(false);
  }, [now]);

  const {
    data: contributionsRaw,
    isLoading: loadingContributions,
    isFetching: fetchingContributions,
  } = useQuery(
    ["my-contributions-page", user?.id],
    async () => {
      const { data } = await api.get("/contributions/my?limit=500");
      return (data.data || []) as any[];
    },
    { enabled: !!user?.id, retry: 1, staleTime: 30000 }
  );

  const createMutation = useMutation(
    async (payload: any) => {
      const { data } = await api.post("/contributions", payload);
      return data;
    },
    {
      onSuccess: () => {
        toast.success("Contribution request submitted successfully");
        queryClient.invalidateQueries(["my-contributions-page"]);
        resetForm();
      },
      onError: (err: any) => {
        toast.error(err?.response?.data?.message || "Failed to submit contribution request");
      },
    }
  );

  const cancelMutation = useMutation(
    async (id: number) => {
      const { data } = await api.delete(`/contributions/${id}`);
      return data;
    },
    {
      onSuccess: () => {
        toast.success("Contribution cancelled");
        queryClient.invalidateQueries(["my-contributions-page"]);
      },
      onError: (err: any) => {
        toast.error(err?.response?.data?.message || "Failed to cancel contribution");
      },
    }
  );

  const salaryContributions = useMemo(() => {
    return (contributionsRaw || []).filter((c: any) =>
      String(c.source || "").toLowerCase() !== "manual_member_contribution"
    );
  }, [contributionsRaw]);

  const totalContributionsPaid = useMemo(() => {
    return salaryContributions
      .filter((c: any) => normalizeStatus(c.status) === "approved")
      .reduce((sum: number, c: any) => sum + safeNumber(c.amount), 0);
  }, [salaryContributions]);

  const paidShares = useMemo(() => {
    return salaryContributions
      .filter((c: any) => normalizeStatus(c.status) === "approved")
      .reduce((sum: number, c: any) => sum + getContributionShares(c), 0);
  }, [salaryContributions, getContributionShares]);

  const pendingContributionsTotal = useMemo(() => {
    return salaryContributions
      .filter((c: any) => normalizeStatus(c.status) === "pending")
      .reduce((sum: number, c: any) => sum + safeNumber(c.amount), 0);
  }, [salaryContributions]);

  const pendingShares = useMemo(() => {
    return salaryContributions
      .filter((c: any) => normalizeStatus(c.status) === "pending")
      .reduce((sum: number, c: any) => sum + getContributionShares(c), 0);
  }, [salaryContributions, getContributionShares]);

  const thisYearTotal = useMemo(() => {
    return salaryContributions
      .filter((c: any) => {
        const d = new Date(c.contributionDate);
        return d.getFullYear() === now.getFullYear() && normalizeStatus(c.status) === "approved";
      })
      .reduce((sum: number, c: any) => sum + safeNumber(c.amount), 0);
  }, [salaryContributions, now]);

  const thisYearShares = useMemo(() => {
    return salaryContributions
      .filter((c: any) => {
        const d = new Date(c.contributionDate);
        return d.getFullYear() === now.getFullYear() && normalizeStatus(c.status) === "approved";
      })
      .reduce((sum: number, c: any) => sum + getContributionShares(c), 0);
  }, [salaryContributions, now, getContributionShares]);

  const daysLeftToDeadline = useMemo(() => {
    const deadline = endOfMonth(now);
    return Math.max(0, differenceInDays(deadline, now));
  }, [now]);

  const thisMonthTotal = useMemo(() => {
    return salaryContributions
      .filter((c: any) => {
        const d = new Date(c.contributionDate);
        return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear() && normalizeStatus(c.status) === "approved";
      })
      .reduce((sum: number, c: any) => sum + safeNumber(c.amount), 0);
  }, [salaryContributions, now]);

  const thisMonthApprovedCount = useMemo(() => {
    return salaryContributions
      .filter((c: any) => {
        const d = new Date(c.contributionDate);
        return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear() && normalizeStatus(c.status) === "approved";
      })
      .reduce((sum: number, c: any) => sum + getContributionShares(c), 0);
  }, [salaryContributions, now, getContributionShares]);

  const monthlyProgress = useMemo(() => {
    if (monthlyCommitment <= 0) return 0;
    return Math.min(100, Math.round((thisMonthTotal / monthlyCommitment) * 100));
  }, [thisMonthTotal, monthlyCommitment]);

  const nextDeductionDate = useMemo(() => {
    return new Date(now.getFullYear(), now.getMonth() + 1, 0);
  }, [now]);

  const deadlinePassed = now.getDate() > 25;

  const contributionRows = useMemo(() => {
    return salaryContributions.map((c: any) => {
      const { approvedBy, approvalDate } = getLastApprovalInfo(c);
      const contributionDate = c.contributionDate || c.createdAt;
      const salaryMonth = c.salaryMonth || contributionDate;
      const monthLabel = c.monthName || (salaryMonth ? format(new Date(salaryMonth), "MMMM yyyy") : "-");
      const statusGroup = normalizeStatus(c.status);
      const shares = getContributionShares(c);
      return {
        ...c,
        monthLabel,
        shares,
        amountValue: safeNumber(c.amount),
        statusGroup,
        statusLabel: c.status || "Pending",
        approvedBy: approvedBy || "-",
        approvalDate: approvalDate || "",
        requestDate: c.createdAt || contributionDate,
        deductionDate: salaryMonth,
        paymentMethod: c.paymentMethod || "Salary Deduction",
      };
    });
  }, [salaryContributions, getContributionShares]);

  const filteredContributions = useMemo(() => {
    const q = historySearch.trim().toLowerCase();
    const statusFilter = historyColumnFilters.find((filter) => filter.id === "statusGroup")?.value as string[] | undefined;
    const methodFilter = historyColumnFilters.find((filter) => filter.id === "paymentMethod")?.value as string[] | undefined;

    const list = contributionRows.filter((row: any) => {
      const matchesSearch = !q || [
        row.monthLabel,
        row.amountValue,
        row.shares,
        row.statusLabel,
        row.paymentMethod,
        row.approvedBy,
      ].join(" ").toLowerCase().includes(q);
      const matchesStatus = !statusFilter?.length || statusFilter.includes(row.statusGroup);
      const matchesMethod = !methodFilter?.length || methodFilter.includes(row.paymentMethod);
      return matchesSearch && matchesStatus && matchesMethod;
    });

    const sort = historySorting[0] || { id: "createdAt", desc: true };
    return [...list].sort((a: any, b: any) => {
      let av = a[sort.id];
      let bv = b[sort.id];
      if (["createdAt", "requestDate", "deductionDate", "approvalDate"].includes(sort.id)) {
        av = av ? new Date(av).getTime() : 0;
        bv = bv ? new Date(bv).getTime() : 0;
      }
      if (typeof av === "string") av = av.toLowerCase();
      if (typeof bv === "string") bv = bv.toLowerCase();
      if (av === bv) return 0;
      const result = av > bv ? 1 : -1;
      return sort.desc ? -result : result;
    });
  }, [contributionRows, historySearch, historyColumnFilters, historySorting]);

  const historyPageCount = Math.max(1, Math.ceil(filteredContributions.length / historyPagination.pageSize));
  const paginatedContributions = useMemo(() => {
    const safePageIndex = Math.min(historyPagination.pageIndex, historyPageCount - 1);
    const start = safePageIndex * historyPagination.pageSize;
    return filteredContributions.slice(start, start + historyPagination.pageSize);
  }, [filteredContributions, historyPageCount, historyPagination.pageIndex, historyPagination.pageSize]);

  const handleSubmit = () => {
    if (!formMonth || !formRequestDate || formAmountNum <= 0 || formShares < 1 || !formDeductionDate) {
      toast.error("Please fill in all required fields");
      return;
    }
    const monthNum = parseInt(formMonth);
    const year = now.getFullYear();
    const contributionDate = new Date(year, monthNum, 1);
    const salaryMonth = new Date(year, monthNum, 1);

    createMutation.mutate({
      amount: formAmountNum,
      shareCount: formShares,
      contributionType: "Monthly",
      contributionDate: format(contributionDate, "yyyy-MM-dd"),
      salaryMonth: format(salaryMonth, "yyyy-MM-dd"),
      paymentMethod: "Salary Deduction",
      source: "salary_deduction",
      notes: `Contribution request for ${MONTHS[monthNum]} ${year}`,
    });
  };

  const handleCancel = (id: number) => {
    cancelMutation.mutate(id);
  };

  const historyColumns = useMemo<ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "monthLabel",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Month" />,
        cell: ({ row }) => <span className="whitespace-nowrap text-sm font-medium text-slate-700">{row.original.monthLabel}</span>,
      },
      {
        accessorKey: "shares",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Shares" />,
        cell: ({ row }) => <span className="whitespace-nowrap text-sm font-semibold text-slate-900">{formatShareCount(Number(row.original.shares || 0))}</span>,
      },
      {
        accessorKey: "amountValue",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Amount" />,
        cell: ({ row }) => <span className="whitespace-nowrap text-sm font-semibold text-slate-900">{safeNumber(row.original.amountValue).toLocaleString()} FRW</span>,
      },
      {
        accessorKey: "requestDate",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Request Date" />,
        cell: ({ row }) => {
          const value = row.original.requestDate;
          return <span className="whitespace-nowrap text-sm text-slate-600">{value ? format(new Date(value), "dd MMM yyyy") : "-"}</span>;
        },
      },
      {
        accessorKey: "deductionDate",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Deduction Date" />,
        cell: ({ row }) => {
          const value = row.original.deductionDate;
          return <span className="whitespace-nowrap text-sm text-slate-600">{value ? format(new Date(value), "dd MMM yyyy") : "-"}</span>;
        },
      },
      {
        accessorKey: "paymentMethod",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Method" />,
        cell: ({ row }) => <span className="whitespace-nowrap text-sm text-slate-600">{row.original.paymentMethod}</span>,
      },
      {
        accessorKey: "statusGroup",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
        cell: ({ row }) => <StatusBadge status={row.original.statusLabel || "Pending"} size="sm" />,
      },
      {
        accessorKey: "approvedBy",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Approved By" />,
        cell: ({ row }) => <span className="whitespace-nowrap text-sm text-slate-600">{row.original.approvedBy || "-"}</span>,
      },
      {
        accessorKey: "approvalDate",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Approval Date" />,
        cell: ({ row }) => {
          const value = row.original.approvalDate;
          return <span className="whitespace-nowrap text-sm text-slate-600">{value ? format(new Date(value), "dd MMM yyyy") : "-"}</span>;
        },
      },
      {
        id: "actions",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Actions" />,
        enableSorting: false,
        cell: ({ row }) => {
          if (row.original.statusGroup !== "pending") {
            return <span className="text-[10px] text-slate-400">-</span>;
          }
          return (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-red-500 hover:bg-red-50 hover:text-red-600 rounded-lg"
              onClick={() => handleCancel(row.original.id)}
              disabled={cancelMutation.isLoading}
            >
              <X className="mr-1 h-3.5 w-3.5" />
              Cancel
            </Button>
          );
        },
      },
    ],
    [cancelMutation.isLoading]
  );

  const historyFacets = useMemo(() => [
    {
      name: "statusGroup",
      title: "Status",
      type: "checkbox",
      options: [
        { label: "Pending", value: "pending" },
        { label: "Approved", value: "approved" },
        { label: "Cancelled", value: "cancelled" },
      ],
    },
    {
      name: "paymentMethod",
      title: "Method",
      type: "checkbox",
      options: [
        { label: "Salary Deduction", value: "Salary Deduction" },
        { label: "Payroll", value: "Payroll" },
        { label: "Cash", value: "Cash" },
        { label: "Bank Transfer", value: "Bank Transfer" },
        { label: "Mobile Money", value: "Mobile Money" },
      ],
    },
  ], []);

  const currentMonthName = format(now, "MMMM yyyy");
  const deadlineDate = format(endOfMonth(now), "dd MMM yyyy");

  const skeletonPage = () => (
    <div className="space-y-6">
      <Skeleton className="h-5 w-48 rounded-lg" />
      <Skeleton className="h-8 w-64 rounded-lg" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map(i => (
          <Skeleton key={i} className="h-[108px] rounded-[16px]" />
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Skeleton className="h-[360px] rounded-[16px]" />
        <Skeleton className="h-[360px] rounded-[16px]" />
      </div>
      <Skeleton className="h-12 w-full rounded-lg" />
      <Skeleton className="h-80 rounded-[16px]" />
    </div>
  );

  if (loadingContributions) return skeletonPage();

  return (
    <div className="space-y-6">
      <Breadcrumb className="mb-1">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink href="/member/dashboard">Dashboard</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>My Contribution</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div>
        <h1 className="text-2xl font-bold text-slate-900">My Contribution</h1>
        <p className="text-sm text-slate-500 mt-0.5">Manage your monthly salary contributions</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border border-emerald-200 bg-emerald-50/40 shadow-sm rounded-[16px] overflow-hidden">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-emerald-100 border border-emerald-200">
                <PiggyBank className="h-6 w-6 text-emerald-600" />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-medium text-emerald-700">Total Contributions Paid</p>
                <p className="text-2xl font-bold text-slate-900 tracking-tight">{totalContributionsPaid.toLocaleString()} <span className="text-sm font-medium text-slate-500">FRW</span></p>
                <p className="text-[11px] text-slate-500 mt-0.5">({formatShareCount(paidShares)} Share{paidShares !== 1 ? "s" : ""})</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-blue-200 bg-blue-50/40 shadow-sm rounded-[16px] overflow-hidden">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-blue-100 border border-blue-200">
                <Clock className="h-6 w-6 text-blue-600" />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-medium text-blue-700">Pending Contributions</p>
                <p className="text-2xl font-bold text-slate-900 tracking-tight">{pendingContributionsTotal.toLocaleString()} <span className="text-sm font-medium text-slate-500">FRW</span></p>
                <p className="text-[11px] text-slate-500 mt-0.5">({formatShareCount(pendingShares)} Share{pendingShares !== 1 ? "s" : ""})</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-orange-200 bg-orange-50/40 shadow-sm rounded-[16px] overflow-hidden">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-orange-100 border border-orange-200">
                <TrendingUp className="h-6 w-6 text-orange-600" />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-medium text-orange-700">Total Contributions This Year</p>
                <p className="text-2xl font-bold text-slate-900 tracking-tight">{thisYearTotal.toLocaleString()} <span className="text-sm font-medium text-slate-500">FRW</span></p>
                <p className="text-[11px] text-slate-500 mt-0.5">({formatShareCount(thisYearShares)} Share{thisYearShares !== 1 ? "s" : ""})</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={cn("border shadow-sm rounded-[16px] overflow-hidden", deadlinePassed ? "border-red-200 bg-red-50/40" : "border-purple-200 bg-purple-50/40")}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={cn("flex h-12 w-12 shrink-0 items-center justify-center rounded-full border", deadlinePassed ? "bg-red-100 border-red-200" : "bg-purple-100 border-purple-200")}>
                <Calendar className={cn("h-6 w-6", deadlinePassed ? "text-red-600" : "text-purple-600")} />
              </div>
              <div className="min-w-0">
                <p className={cn("text-[11px] font-medium", deadlinePassed ? "text-red-700" : "text-purple-700")}>Days Left to Next Deadline</p>
                <p className="text-2xl font-bold text-slate-900 tracking-tight">{String(daysLeftToDeadline)} <span className="text-sm font-medium text-slate-500">days</span></p>
                <p className="text-[11px] text-slate-500 mt-0.5">{deadlinePassed ? "Past deadline" : `Until ${deadlineDate}`}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card className="border border-slate-200 shadow-sm rounded-[16px]">
          <CardHeader className="px-5 pt-5 pb-0">
            <CardTitle className="text-[15px] font-semibold text-slate-900 flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#009A44]/10">
                <HandCoins className="h-3.5 w-3.5 text-[#009A44]" />
              </div>
              Create New Contribution
            </CardTitle>
          </CardHeader>
          <CardContent className="p-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-600">Month <span className="text-red-500">*</span></Label>
                <Select value={formMonth} onValueChange={setFormMonth}>
                  <SelectTrigger className="h-10 text-sm rounded-[10px]">
                    <SelectValue placeholder="Select month" />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((m, i) => (
                      <SelectItem key={i} value={String(i)}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-600">Date of Request <span className="text-red-500">*</span></Label>
                <Input
                  type="date"
                  value={formRequestDate}
                  onChange={(e) => setFormRequestDate(e.target.value)}
                  className="h-10 text-sm rounded-[10px]"
                />
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-medium text-slate-600">Amount (FRW) <span className="text-red-500">*</span></Label>
                  <button
                    type="button"
                    onClick={() => {
                      if (isCustomAmount) {
                        setFormAmount(String(monthlyCommitment));
                        setIsCustomAmount(false);
                      } else {
                        setIsCustomAmount(true);
                      }
                    }}
                    className="text-[11px] font-semibold text-[#009A44] hover:text-[#00803a] transition-colors"
                  >
                    {isCustomAmount ? "Reset to default" : "Change"}
                  </button>
                </div>
                {isCustomAmount ? (
                  <Input
                    type="number"
                    value={formAmount}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === "" || /^\d*\.?\d*$/.test(val)) {
                        setFormAmount(val);
                      }
                    }}
                    placeholder="Enter amount to contribute"
                    min="0"
                    step="1"
                    autoFocus
                    onFocus={(e) => e.target.select()}
                    className="h-10 text-sm rounded-[10px] font-semibold"
                  />
                ) : (
                  <div className="h-10 flex items-center gap-1.5 px-3 border border-slate-200 rounded-[10px] bg-slate-50">
                    <span className="font-semibold text-sm text-slate-900">{formAmountNum.toLocaleString()}</span>
                    <span className="text-xs text-slate-400 font-medium">FRW</span>
                  </div>
                )}
                <p className="text-[10px] text-slate-400">1 Share = {sharePrice.toLocaleString()} FRW</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-600">Number of Shares (Auto)</Label>
                <div className="h-10 flex items-center justify-center border border-slate-200 rounded-[10px] bg-slate-50 font-semibold text-lg tabular-nums">
                  {formatShareCount(formShares)}
                </div>
                <p className="text-[10px] text-slate-400">Calculated from amount / share value</p>
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs font-medium text-slate-600">Contribution Deduction Date <span className="text-red-500">*</span></Label>
                <Input
                  type="date"
                  value={formDeductionDate}
                  onChange={(e) => setFormDeductionDate(e.target.value)}
                  className="h-10 text-sm rounded-[10px] max-w-xs"
                />
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-100 rounded-[10px] px-4 py-2.5 mt-4 flex items-start gap-2.5">
              <Info className="h-4 w-4 text-blue-500 shrink-0 mt-0.5" />
              <p className="text-xs text-blue-600 leading-relaxed">
                This amount will be deducted from your salary on the selected deduction date.
              </p>
            </div>
            <div className="flex items-center gap-3 mt-5">
              <Button
                variant="outline"
                onClick={resetForm}
                className="h-10 px-5 text-sm gap-1.5 rounded-[10px] border-slate-200"
              >
                <X className="h-4 w-4" />
                Clear
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={createMutation.isLoading || formShares < 1}
                className="h-10 px-5 text-sm gap-1.5 rounded-[10px] bg-[#009A44] hover:bg-[#00803a] text-white font-medium"
              >
                {createMutation.isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <HandCoins className="h-4 w-4" />
                )}
                Submit Contribution Request
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-200 shadow-sm rounded-[16px]">
          <CardHeader className="px-5 pt-5 pb-0">
            <CardTitle className="text-[15px] font-semibold text-slate-900 flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#009A44]/10">
                <DollarSign className="h-3.5 w-3.5 text-[#009A44]" />
              </div>
              Contribution Overview
            </CardTitle>
          </CardHeader>
          <CardContent className="p-5">
            <div className="grid grid-cols-3 gap-3 mb-5">
              <div className="bg-slate-50 rounded-[12px] p-3.5">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Monthly Commitment</p>
                <p className="text-lg font-bold text-slate-900 mt-1">{monthlyCommitment.toLocaleString()}</p>
                <p className="text-[10px] text-slate-400 mt-0.5">FRW / month</p>
              </div>
              <div className="bg-slate-50 rounded-[12px] p-3.5">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Deducted From</p>
                <p className="text-lg font-bold text-[#009A44] mt-1">Salary</p>
                <p className="text-[10px] text-slate-400 mt-0.5">payroll deduction</p>
              </div>
              <div className="bg-slate-50 rounded-[12px] p-3.5">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Next Deduction</p>
                <p className="text-lg font-bold text-slate-900 mt-1">{format(nextDeductionDate, "dd MMM")}</p>
                <p className="text-[10px] text-slate-400 mt-0.5">{format(nextDeductionDate, "yyyy")}</p>
              </div>
            </div>

            <div className="mb-4">
              <div className="flex justify-between items-center mb-1.5">
                <span className="text-xs font-medium text-slate-600">Contribution Progress - {format(now, "MMMM yyyy")}</span>
                <span className="text-xs font-semibold text-slate-700">{formatShareCount(thisMonthApprovedCount)} / {formatShareCount(requiredShares)} Paid</span>
              </div>
              <Progress
                value={monthlyProgress}
                className="h-3 bg-slate-100 rounded-full [&>div]:bg-[#009A44] [&>div]:rounded-full"
              />
              <p className="text-[10px] text-slate-400 mt-1">{thisMonthTotal.toLocaleString()} / {monthlyCommitment.toLocaleString()} FRW</p>
            </div>

            {!deadlinePassed && thisMonthTotal < monthlyCommitment && monthlyCommitment > 0 && (
              <Alert className="border-amber-200 bg-amber-50 rounded-[10px] mb-4">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                <AlertDescription className="text-xs text-amber-700">
                  Please submit your contribution before {format(endOfMonth(now), "dd MMM yyyy")}
                </AlertDescription>
              </Alert>
            )}

            {deadlinePassed && (
              <Alert className="border-red-200 bg-red-50 rounded-[10px] mb-4">
                <AlertTriangle className="h-4 w-4 text-red-500" />
                <AlertDescription className="text-xs text-red-700">
                  Deadline passed. Submit your contribution as soon as possible.
                </AlertDescription>
              </Alert>
            )}

            <div className="flex items-center justify-between pt-1">
              <Button
                variant="ghost"
                size="sm"
                className="text-xs text-slate-500 hover:text-slate-700 h-8 gap-1.5 px-2"
                onClick={() => navigate("/member/settings")}
              >
                <Calendar className="h-3.5 w-3.5" />
                Contribution Policy
              </Button>
              <Button
                size="sm"
                className="h-9 px-4 text-xs gap-1.5 rounded-[10px] bg-[#009A44] hover:bg-[#00803a] text-white font-medium"
                onClick={() => {
                  const el = document.getElementById("contributions-history");
                  el?.scrollIntoView({ behavior: "smooth" });
                }}
              >
                View Contribution History
                <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card id="contributions-history" className="border border-slate-200 shadow-sm rounded-[16px]">
        <CardHeader className="px-5 pt-5 pb-0">
          <CardTitle className="text-[15px] font-semibold text-slate-900">My Contributions History</CardTitle>
        </CardHeader>
        <CardContent className="p-5 pt-3">
          <DataTable
            columns={historyColumns}
            data={paginatedContributions}
            facets={historyFacets}
            isLoading={loadingContributions}
            isFetching={fetchingContributions}
            onSearch={(value: string) => {
              setHistorySearch(value);
              setHistoryPagination((prev) => ({ ...prev, pageIndex: 0 }));
            }}
            columnFilters={historyColumnFilters}
            setColumnFilters={(next) => {
              setHistoryColumnFilters(next);
              setHistoryPagination((prev) => ({ ...prev, pageIndex: 0 }));
            }}
            sorting={historySorting}
            setSorting={setHistorySorting}
            setPagination={setHistoryPagination}
            pageIndex={Math.min(historyPagination.pageIndex, historyPageCount - 1)}
            pageSize={historyPagination.pageSize}
            pageCount={historyPageCount}
            enableExport={false}
            emptyMessage="No contributions found"
            emptyDescription="Submit your first contribution request to get started."
            loadingRows={5}
          />
        </CardContent>
      </Card>
    </div>
  );
}
